import { useMemo, useState } from "react";
import { Bookmark, Filter, Search, Trash2 } from "lucide-react";
import { Button } from "../../../components/Button";
import { Chip } from "../../../components/Chip";
import { FormField } from "../../../components/FormField";
import type { JobCategory, JobUrgency } from "../../../types";

export type JobFilterState = {
  search: string;
  category: "ALL" | JobCategory;
  urgency: "ALL" | JobUrgency;
  readiness: "ALL" | "RED" | "AMBER" | "GREEN";
};

const categories: JobFilterState["category"][] = [
  "ALL",
  "ASSEMBLY",
  "BASIC_LABOR",
  "DRYWALL_PAINT",
  "ELECTRICAL",
  "HANDYMAN",
  "LANDSCAPE",
  "MOVING",
  "PLUMBING",
  "PROPERTY_MAINTENANCE",
];

const urgencies: JobFilterState["urgency"][] = ["ALL", "ROUTINE", "SOON", "URGENT", "EMERGENCY"];
const readinessOptions: JobFilterState["readiness"][] = ["ALL", "GREEN", "AMBER", "RED"];
const resetFilters: JobFilterState = {
  search: "",
  category: "ALL",
  urgency: "ALL",
  readiness: "ALL",
};
const savedViewPrefix = "clearscope.jobs.savedView.";

type SavedView = {
  key: string;
  name: string;
  filters: JobFilterState;
};

function readSavedViews(): SavedView[] {
  return Object.keys(localStorage)
    .filter((key) => key.startsWith(savedViewPrefix))
    .sort()
    .flatMap((key) => {
      try {
        const parsed = JSON.parse(localStorage.getItem(key) ?? "{}") as {
          name?: string;
          filters?: JobFilterState;
        };
        if (!parsed.filters) {
          return [];
        }
        return [{ key, name: parsed.name ?? "Saved view", filters: parsed.filters }];
      } catch {
        return [];
      }
    });
}

export function JobFilters({
  filters,
  onChange,
  onSaveView,
}: {
  filters: JobFilterState;
  onChange: (filters: JobFilterState) => void;
  onSaveView?: () => void;
}) {
  const [savedViews, setSavedViews] = useState<SavedView[]>(() => readSavedViews());
  const activeFilters = useMemo(
    () =>
      [
        filters.search ? `Search: ${filters.search}` : null,
        filters.category !== "ALL" ? filters.category.replace(/_/g, " ") : null,
        filters.urgency !== "ALL" ? filters.urgency : null,
        filters.readiness !== "ALL" ? `Detail: ${filters.readiness}` : null,
      ].filter(Boolean) as string[],
    [filters],
  );

  function saveCurrentView() {
    const index = savedViews.length + 1;
    const name = `View ${index}`;
    localStorage.setItem(`${savedViewPrefix}${Date.now()}`, JSON.stringify({ name, filters }));
    setSavedViews(readSavedViews());
    onSaveView?.();
  }

  function deleteView(key: string) {
    localStorage.removeItem(key);
    setSavedViews(readSavedViews());
  }

  return (
    <aside
      className="card-inset grid content-start gap-5 p-5 [contain:paint] lg:sticky lg:top-24"
      aria-label="Job filters"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-text">
            <Filter aria-hidden="true" className="h-4 w-4 text-accent-700" strokeWidth={1.5} />
            Filters
          </h2>
          <p className="mt-1 text-sm leading-6 text-text-muted">
            All filtering is local to this browser.
          </p>
        </div>
        <Button
          className="h-auto px-0"
          size="sm"
          type="button"
          variant="link"
          onClick={() => onChange(resetFilters)}
        >
          Reset
        </Button>
      </div>

      {activeFilters.length ? (
        <div className="flex flex-wrap gap-2 rounded-card border border-border bg-paper p-3">
          {activeFilters.map((filter) => (
            <Chip key={filter}>{filter}</Chip>
          ))}
        </div>
      ) : null}

      <div className="grid gap-4 border-t border-border pt-5">
        <div>
          <FormField
            id="job-search"
            label="Search jobs"
            leftIcon={<Search aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />}
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.currentTarget.value })}
          />
        </div>
        <div>
          <FormField
            id="job-category"
            label="Category"
            kind="select"
            value={filters.category}
            onChange={(event) =>
              onChange({
                ...filters,
                category: event.currentTarget.value as JobFilterState["category"],
              })
            }
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category.replace(/_/g, " ")}
              </option>
            ))}
          </FormField>
        </div>
        <div>
          <FormField
            id="job-urgency"
            label="Urgency"
            kind="select"
            value={filters.urgency}
            onChange={(event) =>
              onChange({
                ...filters,
                urgency: event.currentTarget.value as JobFilterState["urgency"],
              })
            }
          >
            {urgencies.map((urgency) => (
              <option key={urgency} value={urgency}>
                {urgency}
              </option>
            ))}
          </FormField>
        </div>
        <div>
          <FormField
            id="job-readiness"
            label="Detail"
            kind="select"
            value={filters.readiness}
            onChange={(event) =>
              onChange({
                ...filters,
                readiness: event.currentTarget.value as JobFilterState["readiness"],
              })
            }
          >
            {readinessOptions.map((readiness) => (
              <option key={readiness} value={readiness}>
                {readiness}
              </option>
            ))}
          </FormField>
        </div>
      </div>

      <Button size="sm" type="button" variant="secondary" onClick={saveCurrentView}>
        <Bookmark aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
        Save view
      </Button>
      <div className="border-t border-border pt-4">
        <p className="text-sm font-semibold text-text">Saved views</p>
        {savedViews.length ? (
          <div className="mt-3 grid gap-2">
            {savedViews.map((view) => (
              <div
                key={view.key}
                className="flex items-center justify-between gap-2 rounded-[12px] border border-border bg-paper p-2"
              >
                <button
                  className="min-h-9 flex-1 rounded-[10px] px-2 text-left text-sm font-semibold text-text transition hover:bg-surface-muted"
                  type="button"
                  onClick={() => onChange(view.filters)}
                >
                  {view.name}
                </button>
                <Button
                  aria-label={`Delete ${view.name}`}
                  size="sm"
                  type="button"
                  variant="ghost"
                  onClick={() => deleteView(view.key)}
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-3 rounded-card border border-border bg-surface-raised p-3 text-sm leading-6 text-text-muted">
            No saved views yet.
          </div>
        )}
      </div>
    </aside>
  );
}
